import React, { useState, useEffect } from 'react'
import StyledButton from '../../generic/StyledButton'
import DraggableModal from '../../generic/DraggableModal'
import Typography from '@mui/material/Typography'
import { toPrice } from '../../../helpers/stringFormat'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Unstable_Grid2'
import Tooltip from '@mui/material/Tooltip'
import { backendPostWithoutRefresh } from '../../../helpers/callBackend'
import { v4 as uuidv4 } from 'uuid'
import { message } from 'antd'
import PermissionGate from '../../../helpers/PermissionGate'
import GenerateCodeV2 from '../../formItems/GenerateCodeV2'
import checkFlag from '../../../helpers/featureFlags'

const ReturnToSenderModal = ({
  visible,
  handleCancelModal,
  paymentRequest,
  failureObject,
  dwollaBalance,
  mode,
  updatePaymentRequests,
}) => {
  const [senderName, setSenderName] = useState(
    paymentRequest.sender_name ? paymentRequest.sender_name : 'this bank account'
  )
  const [counterPartyName, setCounterPartyName] = useState(
    paymentRequest.counterparty_name ? paymentRequest.counterparty_name : 'Unknown'
  )
  const [paymentDescription, setPaymentDescription] = useState(
    paymentRequest.description ? paymentRequest.description : 'Unknown'
  )
  const [amount, setAmount] = useState(paymentRequest.amount || 0)
  const [returnCode, setReturnCode] = useState(failureObject.code ? failureObject.code : 'Unknown')
  const [returnReason, setReturnReason] = useState(failureObject.description ? failureObject.description : 'Unknown')
  const [dwollaBalanceAmount, setDwollaBalanceAmount] = useState(
    dwollaBalance?.total?.value ? dwollaBalance.total.value : 0
  )
  const [verificationToken, setVerificationToken] = useState('')
  const [otp, setOtp] = useState('')
  const idempotencyKey = uuidv4()

  const [formData, setFormData] = useState({
    name: '',
    routingNumber: '',
    accountNumber: '',
  })

  const handleInput = (event, field) => {
    let newForm = { ...formData }
    newForm[field] = event.target.value
    setFormData(newForm)
  }

  const handleSubmit = async () => {
    if (parseFloat(amount) > parseFloat(dwollaBalanceAmount)) {
      message.error('Insufficient funds. Amount exceeds the available balance.')
      return
    }

    if (mode === 'retry') {
      for (let field in formData) {
        if (!formData[field]) {
          message.error(`Please enter a recipient ${field}`)
          return
        }
      }
    }

    try {
      const paymentsUrl = `/api/payments/payment-request/${mode}/`
      const paymentResp = await backendPostWithoutRefresh(paymentsUrl, {
        payment_request_id: paymentRequest.id,
        idempotency_key: idempotencyKey,
        otp: otp,
        verificationToken: verificationToken,
        ...formData,
      })

      updatePaymentRequests(paymentResp.data?.data?.created, 'replace')
      updatePaymentRequests(paymentResp.data?.data?.canceled, 'remove')
    } catch (error) {
      console.error('error')
    }
  }

  useEffect(() => {
    setDwollaBalanceAmount(dwollaBalance?.total?.value ? dwollaBalance.total.value : 0)
  }, [dwollaBalance])
  useEffect(() => {
    setSenderName(paymentRequest.sender_name ? paymentRequest.sender_name : 'this bank account')
    setCounterPartyName(paymentRequest.counterparty_name ? paymentRequest.counterparty_name : 'Unknown')
    setPaymentDescription(paymentRequest.description ? paymentRequest.description : 'Unknown')
    setAmount(paymentRequest.amount)
  }, [paymentRequest])
  useEffect(() => {
    setReturnCode(failureObject.code ? failureObject.code : 'Unknown')
    setReturnReason(failureObject.description ? failureObject.description : 'Unknown')
  }, [failureObject])

  return (
    <DraggableModal
      modalTitle={`${mode.charAt(0).toUpperCase() + mode.slice(1)} Payment to ${counterPartyName} to ${senderName}`}
      modalVisible={visible}
      handleCancelModal={handleCancelModal}
      width='50%'
      content={
        <>
          <Typography>{`The payment to ${counterPartyName} from ${senderName} was returned. To retry payment, you will need to update the recipient's ACH details.`}</Typography>
          <List
            disableGutters
            sx={{
              '& .MuiListItem-root': {
                py: 0,
                pl: 0,
              },
            }}
          >
            <ListItem>
              <ListItemText primaryTypographyProps={{ variant: 'h5' }} primary='Return Details' />
            </ListItem>
            <List sx={{ pl: 4, py: 0 }}>
              <ListItem>
                <ListItemText primary={`Return Code: ${returnCode} ${returnReason}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Amount to ${mode === 'retry' ? 'send' : 'return'}: ${toPrice(amount)}`} />
              </ListItem>
              <ListItem>
                <ListItemText
                  sx={{ flex: '0 0 auto' }}
                  primary={`Available to ${mode === 'retry' ? 'send' : 'return'}: ${toPrice(dwollaBalanceAmount)}`}
                />
                <Tooltip
                  arrow
                  placement='right-start'
                  title={
                    <Typography>
                      {`Your payment has been debited from your account. It was returned upon crediting your receiving
                      party. You have ${toPrice(
                        dwollaBalanceAmount
                      )} available to immediately send back to your account or your receiving party`}
                    </Typography>
                  }
                >
                  <Typography sx={{ marginLeft: '10px' }} fontStyle={'italic'} color={'text.disabled'}>
                    What is this?
                  </Typography>
                </Tooltip>
              </ListItem>
            </List>
            <ListItem>
              <ListItemText primaryTypographyProps={{ variant: 'h5' }} primary='Original Payment Details' />
            </ListItem>
            <List sx={{ pl: 4, py: 0 }}>
              <ListItem>
                <ListItemText primary={`Send from: ${senderName}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Send to: ${counterPartyName}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Description: ${paymentDescription}`} />
              </ListItem>
              <ListItem>
                <ListItemText primary={`Amount: ${toPrice(amount)}`} />
              </ListItem>
            </List>
            {mode === 'retry' && (
              <>
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'h5' }}
                    primary={`New ACH Details for ${counterPartyName}`}
                  />
                </ListItem>
                <Grid container spacing={1} direction='column' sx={{ pl: 4 }}>
                  <Grid container item alignItems='center'>
                    <Grid item xs={2}>
                      <Typography>Name</Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0px',
                          },
                        }}
                        size='small'
                        onChange={(event) => handleInput(event, 'name')}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item alignItems='center'>
                    <Grid item xs={2}>
                      <Typography>Account No.</Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0px',
                          },
                        }}
                        size='small'
                        onChange={(event) => handleInput(event, 'accountNumber')}
                      />
                    </Grid>
                  </Grid>
                  <Grid container item alignItems='center'>
                    <Grid item xs={2}>
                      <Typography>Routing No.</Typography>
                    </Grid>
                    <Grid item>
                      <TextField
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0px',
                          },
                        }}
                        size='small'
                        onChange={(event) => handleInput(event, 'routingNumber')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </List>
          {checkFlag('otpPayments') && (
            <GenerateCodeV2
              setOtp={setOtp}
            />
          )}
        </>
      }
      actions={
        <PermissionGate
          requiredRole='admin'
          readOnly={
            <StyledButton label='Approve and Submit Payment' disabled={true} fontSize={14} customTheme='outlined' />
          }
          toolTipText='Only admins can initiate returns and retries'
          toolTipPlacement='top'
          spanSettings={{ style: { width: '100%' } }}
        >
          <StyledButton label='Approve and Submit Payment' fontSize={14} onClick={handleSubmit} />
        </PermissionGate>
      }
    />
  )
}

export default ReturnToSenderModal
